import BookingBlock from 'components/umbraco/BookingBlock';
import CDBlock from 'components/umbraco/CDBlock';
import ContactFormBlock from 'components/umbraco/ContactFormBlock';
import CTABlock from 'components/umbraco/CTABlock';
import DynamicFAQBlock from 'components/umbraco/DynamicFAQBlock';
import FacilityCarouselBlock from 'components/umbraco/FacilityCarouselBlock';
import FAQBlock from 'components/umbraco/FAQBlock';
import FAQSquareBlock from 'components/umbraco/FAQSquareBlock';
import HeroBlock from 'components/umbraco/HeroBlock';
import HeroImage from 'components/umbraco/HeroImage';
import HighlightsBlock from 'components/umbraco/HighlightsBlock';
import IFrameBlock from 'components/umbraco/IFrameBlock';
import ImageBlock from 'components/umbraco/ImageBlock';
import SquaresBlock from 'components/umbraco/SquaresBlock';
import TextBlock from 'components/umbraco/TextBlock';
import TwoColumnImageAndTextBlock from 'components/umbraco/TwoColumnImageAndTextBlock';
import {
  BlockItem,
  BlockProps,
  BlocksResponse,
  BookingProps,
  CDBlockProps,
  ContactFormProps,
  ContentPage,
  CtaProps,
  DynamicFAQProps,
  FacilitiesArchiveContent,
  FacilityArchive,
  FacilityCarouselProps,
  FacilityContent,
  FAQProps,
  FAQSquareProps,
  HeroProps,
  HighlightsProps,
  IFrameProps,
  ImageProps,
  MainMenu,
  PageMetaData,
  ProductInformationModel,
  SquaresProps,
  StartPage,
} from 'types/umbraco';

export interface UmbracoRouteNameItem {
  path: string;
  name: string;
}

async function umbracoGet(path: string) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const resp = await fetch(
      `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/` + path,
      { signal: controller.signal },
    );

    clearTimeout(timeoutId);

    const toRichText = (value: any) => {
      if (!value) return null;
      if (typeof value === 'string') return { markup: value };
      return value;
    };
    if (resp.ok) {
      return await resp.json();
    }
    return null;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error(`Umbraco API timeout for path ${path}`);
    } else {
      console.error(`Umbraco API error for path ${path}:`, error.message);
    }
    return null;
  }
}

export async function getStartpage(): Promise<StartPage> {
  const startpage = await umbracoGet(
    `content/item/${process.env.NEXT_PUBLIC_UMBRACO_STARTPAGE_ID}`,
  );

  if (!startpage) return null;

  const blocks = await formatBlocks(startpage.properties.blocks);

  return {
    blocks,
    meta: pageMetaDataFromUmbNode(startpage),
  };
}

export async function getFacilitiesArchiveContent(): Promise<
  FacilitiesArchiveContent | null
> {
  return umbracoGet('content/item/hyr-forrad');
}

export async function getFacilityArchive(): Promise<FacilityArchive> {
  const facilityArchive = await umbracoGet(
    `content/item/1ce33c60-0798-4d1d-b9fb-fb36a33ad4d7`,
  );

  if (!facilityArchive) return null;

  return facilityArchive;
}

export async function getContentPage(slug: string): Promise<ContentPage> {
  const contentpage = await umbracoGet(`content/item/${slug}`);

  if (!contentpage) {
    return null;
  }

  const blocks = await formatBlocks(contentpage.properties.blocks);

  return {
    blocks,
    meta: pageMetaDataFromUmbNode(contentpage),
  };
}

export async function getFooterMenu(): Promise<MainMenu> {
  const data = await umbracoGet(
    `content/item/${process.env.NEXT_PUBLIC_UMBRACO_STARTPAGE_ID}`,
  );
  if (!data || !data.properties.footerMenu) return null;

  return {
    items: data.properties.footerMenu.map(
      (x: { id: string; name: string; route?: { path: string } }) => {
        return {
          path: cleanUrl(x.route?.path) ?? '',
          name: x.name,
          id: x.id,
        };
      },
    ),
  };
}

export async function getBottomMenu(): Promise<MainMenu> {
  const data = await umbracoGet(
    `content/item/${process.env.NEXT_PUBLIC_UMBRACO_STARTPAGE_ID}`,
  );
  if (!data || !data.properties.bottomMenu) return null;

  return {
    items: data.properties.bottomMenu.map(
      (x: { id: string; name: string; route?: { path: string } }) => {
        return {
          path: cleanUrl(x.route?.path) ?? '',
          name: x.name,
          id: x.id,
        };
      },
    ),
  };
}

export async function getFooterText(): Promise<string> {
  const data = await umbracoGet(
    `content/item/${process.env.NEXT_PUBLIC_UMBRACO_STARTPAGE_ID}`,
  );
  if (!data || !data.properties.footerText) return null;

  return data.properties.footerText.markup;
}

function cleanUrl(url: string) {
  return url.replace(/^\/+|\/+$/g, '');
}

// Builds an absolute Umbraco media URL and requests it in WebP format
export function getUmbracoImageUrl(url?: string): string {
  if (!url) return url;

  const absoluteUrl = url.startsWith('http')
    ? url
    : `${process.env.NEXT_PUBLIC_UMBRACO_URL}${url.startsWith('/') ? url : `/${url}`}`;

  const separator = absoluteUrl.includes('?') ? '&' : '?';
  return `${absoluteUrl}${separator}format=webp`;
}

export async function getAllContentPagePaths(): Promise<
  { url: string; level: number; id: string }[]
> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const resp = await fetch(
      `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content?take=1000&culture=sv-SE`,
      { signal: controller.signal },
    );

    clearTimeout(timeoutId);

    const data = await resp.json();
    return data.items
      .filter((x) => x.contentType === 'contentPage')
      .map((x: any) => ({
        url: cleanUrl(x.route?.path),
      }));
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Timeout fetching content page paths');
    } else {
      console.error('Error fetching content page paths:', error.message);
    }
    return [];
  }
}

export async function getFlattenedUmbracoRouteNames(): Promise<
  UmbracoRouteNameItem[]
> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const resp = await fetch(
      `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content?take=1000&culture=sv-SE`,
      { signal: controller.signal },
    );

    clearTimeout(timeoutId);

    if (!resp.ok) {
      return [];
    }

    const data = await resp.json();
    const items = Array.isArray(data?.items) ? data.items : [];

    return items
      .map((item: any) => {
        const routePath = item?.route?.path;
        const name = item?.name;

        if (typeof routePath !== 'string' || typeof name !== 'string') {
          return null;
        }

        return {
          path: routePath,
          name,
        } as UmbracoRouteNameItem;
      })
      .filter((item): item is UmbracoRouteNameItem => item !== null);
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Timeout fetching flattened Umbraco route names');
    } else {
      console.error(
        'Error fetching flattened Umbraco route names:',
        error.message,
      );
    }
    return [];
  }
}

async function getCountyName(facility: any): Promise<string | undefined> {
  if (!facility?.id) {
    return undefined;
  }

  const ancestors = await umbracoGet(
    `content?fetch=ancestors:${encodeURIComponent(facility.id)}` +
      `&take=100&culture=sv-SE&expand=properties%5B%24all%5D`,
  );
  const county = ancestors?.items?.find(
    (ancestor: any) =>
      typeof ancestor.properties?.type === 'string' &&
      ancestor.properties.type.trim().toLowerCase() === 'län',
  );

  return county?.name;
}

async function getFacilityDetails(facility: any): Promise<any> {
  if (!facility?.id) {
    return facility;
  }

  return (
    (await umbracoGet(
      `content/item/${encodeURIComponent(facility.id)}` +
        `?expand=properties%5B%24all%5D`,
    )) ?? facility
  );
}

async function formatBlocks(blocks: BlocksResponse): Promise<BlockItem[]> {
  if (!blocks) {
    return;
  }

  const faqInfo = blocks.items.some(
    (block) => block.content.contentType === 'dynamicFaqBlock',
  )
    ? umbracoGet('content/item/faqinfo')
    : null;

  return Promise.all(
    blocks.items.map(async (block) => {
      const mediaArray = block.content.properties.blockMedia;
      if (mediaArray?.length) {
        block.content.properties.blockMedia = mediaArray.map((media) => ({
          ...media,
          url: getUmbracoImageUrl(media.url),
        }));
      }

      if (block.content.contentType === 'dynamicFaqBlock') {
        const properties = block.content.properties as DynamicFAQProps;
        const faqInfoContent = await faqInfo;
        properties.title =
          properties.title ?? faqInfoContent?.properties?.title ?? null;
        properties.preamble =
          properties.preamble ?? faqInfoContent?.properties?.preamble ?? null;

        if (Array.isArray(properties.faqs)) {
          const faqs = await Promise.all(
            properties.faqs.map(async (faq) => {
              if (faq.properties?.question && faq.properties?.answer) {
                return faq;
              }

              if (!faq.id) {
                return null;
              }

              return umbracoGet(
                `content/item/${encodeURIComponent(faq.id)}` +
                  `?expand=properties%5B%24all%5D`,
              );
            }),
          );

          properties.faqs = faqs.filter(
            (faq) => faq?.contentType === 'faqItem',
          );
        }
      }

      if (block.content.contentType === 'facilityCarouselBlock') {
        const properties = block.content.properties as FacilityCarouselProps;
        const facilitys = properties.facilitys;
        if (Array.isArray(facilitys)) {
          const facilityDetails = await Promise.all(
            facilitys.map((facility) => getFacilityDetails(facility)),
          );

          properties.facilitys = await Promise.all(
            facilityDetails
              .filter((facility) => {
                const routePath = normalizeRoutePath(facility?.route?.path);

                return routePath.startsWith('hyr-forrad/');
              })
              .map(async (facility) => {
                const images = formatImages(facility.properties?.image);

                return {
                  ...facility,
                  properties: {
                    ...facility.properties,
                    image: images,
                  },
                  image: images[0] ?? null,
                  url: facility?.route?.path,
                  countyName: await getCountyName(facility),
                };
              }),
          );
        }
      }

      return block;
    }),
  );
}

export function BlockRenderer({ items }: BlockProps): JSX.Element {
  if (!items) {
    return null;
  }
  return (
    <div>
      {items.map((x, i) => {
        const normalizedBlock = normalizeBlockImages(x);
        switch (x.content.contentType) {
          case 'heroBlock':
            return (
              <HeroBlock
                key={i}
                {...(normalizedBlock.content.properties as HeroProps)}
              />
            );

          case 'ctaBlock':
            return (
              <CTABlock
                key={i}
                {...(normalizedBlock.content.properties as CtaProps)}
              />
            );

          case 'squaresBlock':
            return (
              <SquaresBlock
                key={i}
                {...(normalizedBlock.content.properties as SquaresProps)}
              />
            );

          case 'highlightsBlock':
            return (
              <HighlightsBlock
                key={i}
                {...(normalizedBlock.content.properties as HighlightsProps)}
              />
            );

          case 'faqBlock':
            return (
              <FAQBlock
                key={i}
                {...(normalizedBlock.content.properties as FAQProps)}
              />
            );

          case 'dynamicFaqBlock':
            return (
              <DynamicFAQBlock
                key={i}
                {...(normalizedBlock.content.properties as DynamicFAQProps)}
              />
            );

          case 'cookieDeclarationBlock':
            return (
              <CDBlock
                key={i}
                {...(normalizedBlock.content.properties as CDBlockProps)}
              />
            );

          case 'textBlock':
            return (
              <TextBlock
                key={i}
                {...(normalizedBlock.content.properties as any)}
              />
            );

          case 'iframeBlock':
            return (
              <IFrameBlock
                key={i}
                {...(normalizedBlock.content.properties as IFrameProps)}
              />
            );

          case 'faqSquareBlock':
            return (
              <FAQSquareBlock
                key={i}
                {...(normalizedBlock.content.properties as FAQSquareProps)}
              />
            );

          case 'contactFormBlock':
            return (
              <ContactFormBlock
                key={i}
                {...(normalizedBlock.content.properties as ContactFormProps)}
              />
            );

          case 'BookingBlock':
            return (
              <BookingBlock
                key={i}
                {...(normalizedBlock.content.properties as BookingProps)}
              />
            );

          case 'imageBlock':
            return (
              <ImageBlock
                key={i}
                {...(normalizedBlock.content.properties as ImageProps)}
              />
            );

          case 'facilityCarouselBlock':
            return (
              <FacilityCarouselBlock
                key={i}
                {...(normalizedBlock.content
                  .properties as FacilityCarouselProps)}
              />
            );

          case 'twoColumnImageAndTextBlock':
            return (
              <TwoColumnImageAndTextBlock
                key={i}
                {...(normalizedBlock.content.properties as any)}
              />
            );

          case 'heroImage':
            return (
              <HeroImage
                key={i}
                {...(normalizedBlock.content.properties as any)}
              />
            );
          default:
            break;
        }
      })}
    </div>
  );
}

export async function GetProductInformation(
  slug: string,
): Promise<ProductInformationModel> {
  const [garageAndParkingInformation, unitInformation] = await Promise.all([
    umbracoGet(`content/item/produktinformation`),
    umbracoGet(`content/item/produktinformation/${slug}`),
  ]);

  if (!unitInformation || !garageAndParkingInformation) return null;

  return {
    unitProductInformation: unitInformation,
    garageAndParkingInformation: garageAndParkingInformation,
  };
}

export async function GetFacility(slug: string): Promise<FacilityContent> {
  let facilityContent = await umbracoGet(`content/item/anlaggningar/${slug}`);

  if (!facilityContent) return null;
  facilityContent.meta = pageMetaDataFromUmbNode(facilityContent);
  facilityContent.properties.image = formatImages(
    facilityContent.properties.image,
  );
  return facilityContent;
}
export async function GetFacilityUmbraco(
  slug: string,
): Promise<FacilityContent> {
  let facilityContent = await umbracoGet(
    `content/item/${slug}?expand=properties[$all]`,
  );

  if (!facilityContent) return null;
  facilityContent.meta = pageMetaDataFromUmbNode(facilityContent);
  facilityContent.properties.image = formatImages(
    facilityContent.properties.image,
  );
  facilityContent.properties.portraitImage = formatImages(
    facilityContent.properties.portraitImage,
  );
  facilityContent.properties.landscapeContent = formatImages(
    facilityContent.properties.landscapeContent,
  );

  await formatBlocks(facilityContent.properties.blocks);
  return facilityContent;
}

export const GetFacilitiesContent = async (): Promise<any> => {
  const returnObj: {
    facilities: any[] | null;
    content: any | null;
    meta: PageMetaData | null;
  } = {
    facilities: null,
    content: null,
    meta: null,
  };

  const readJson = async (url: string) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        return null;
      }

      const text = await response.text();
      if (!text) {
        return null;
      }

      return JSON.parse(text);
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') {
        console.error(`Timeout fetching Umbraco data: ${url}`);
      } else {
        console.error(`Error parsing Umbraco data: ${url}`);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const facilitiesJson = await readJson(
    `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content` +
      `?filter=contentType:facility` +
      `&sort=name:asc` +
      `&take=1000&culture=sv-SE`,
  );

  if (!facilitiesJson?.items?.length) {
    returnObj.facilities = [];
    return returnObj;
  }

  returnObj.facilities = facilitiesJson.items.map((x: any) => {
    const images = formatImages(x.properties?.image);

    return {
      id: x.id,
      name: x.name,
      address: x.properties?.address,
      url: x.route?.path,
      image: images.length > 0 ? images[0] : null,
      type: x.properties?.type,
    };
  });

  const contentJson = await readJson(
    `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content/item/${process.env.NEXT_PUBLIC_UMBRACO_FACILITYPAGE_ID}`,
  );

  if (!contentJson) {
    return returnObj;
  }

  returnObj.content = {
    id: contentJson.id,
    header: contentJson.properties?.header ?? null,
    rteText: contentJson.properties?.rteText ?? null,
  };

  returnObj.meta = pageMetaDataFromUmbNode(contentJson);

  return returnObj;
};

export type FacilityContentTypeFilter = 'Anläggning' | 'Stad' | 'Lan';

export const GetFacilitiesContentNew = async (
  facilityType?: FacilityContentTypeFilter,
): Promise<any> => {
  const returnObj: {
    facilities: any[] | null;
    content: any | null;
    meta: PageMetaData | null;
  } = {
    facilities: null,
    content: null,
    meta: null,
  };

  const readJson = async (url: string) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        return null;
      }

      const text = await response.text();
      if (!text) {
        return null;
      }

      return JSON.parse(text);
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') {
        console.error(`Timeout fetching Umbraco data: ${url}`);
      } else {
        console.error(`Error parsing Umbraco data: ${url}`);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const facilitiesJson = await readJson(
    `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content` +
      `?filter=contentType:facility` +
      `&sort=name:asc` +
      `&take=1000&culture=sv-SE`,
  );

  if (!facilitiesJson?.items?.length) {
    returnObj.facilities = [];
    return returnObj;
  }

  returnObj.facilities = facilitiesJson.items
    .filter((x: any) => {
      const routePath = normalizeRoutePath(x.route?.path);
      if (routePath !== 'hyr-forrad' && !routePath.startsWith('hyr-forrad/')) {
        return false;
      }

      if (!facilityType) {
        return true;
      }

      return normalizeFacilityType(x.properties?.type) === facilityType;
    })
    .map((x: any) => {
      const images = formatImages(x.properties?.image);

      return {
        id: x.id,
        name: x.name,
        address: x.properties?.address,
        url: x.route?.path,
        image: images.length > 0 ? images[0] : null,
        type: x.properties?.type,
      };
    });

  const contentJson = await readJson(
    `${process.env.NEXT_PUBLIC_UMBRACO_URL}/umbraco/delivery/api/v2/content/item/1ce33c60-0798-4d1d-b9fb-fb36a33ad4d7`,
  );

  if (!contentJson) {
    return returnObj;
  }

  returnObj.content = {
    id: contentJson.id,
    header: contentJson.properties?.header ?? null,
    rteText: contentJson.properties?.rteText ?? null,
  };

  returnObj.meta = pageMetaDataFromUmbNode(contentJson);

  return returnObj;
};

function pageMetaDataFromUmbNode(pageData): PageMetaData {
  return {
    title: pageData?.properties.alternativeTitle ?? '',
    description: pageData?.properties.metaDescription ?? '',
    nodeName: pageData?.name ?? '',
    canonical: pageData?.properties.canonical ?? '',
  };
}

function normalizeMarkupImages(markup?: string): string {
  if (!markup || typeof markup !== 'string') {
    return markup;
  }

  return markup.replace(
    /src="(?!https?:\/\/)([^"]+)"/g,
    (match, url) => `src="${getUmbracoImageUrl(url)}"`,
  );
}

function normalizeBlockImages(block: any): any {
  const props = block?.content?.properties;
  if (!props) return block;

  if (props.nestedBlocks?.items) {
    props.nestedBlocks.items = props.nestedBlocks.items.map((item: any) => {
      if (item.content.properties.blockIcon) {
        item.content.properties.blockIcon = formatImages(
          Array.isArray(item.content.properties.blockIcon)
            ? item.content.properties.blockIcon
            : [item.content.properties.blockIcon],
        )[0];
      }
      if (item.content.properties.blockImage) {
        item.content.properties.blockImage = formatImages(
          Array.isArray(item.content.properties.blockImage)
            ? item.content.properties.blockImage
            : [item.content.properties.blockImage],
        )[0];
      }
      if (item.content.properties.text?.markup) {
        item.content.properties.text.markup = normalizeMarkupImages(
          item.content.properties.text.markup,
        );
      }
      return item;
    });
  }
  if (props.nestedHighlights?.items) {
    props.nestedHighlights.items = props.nestedHighlights.items.map(
      (item: any) => {
        if (item.content.properties.highlightImage) {
          item.content.properties.highlightImage = formatImages(
            Array.isArray(item.content.properties.highlightImage)
              ? item.content.properties.highlightImage
              : [item.content.properties.highlightImage],
          )[0];
        }
        if (item.content.properties.text?.markup) {
          item.content.properties.text.markup = normalizeMarkupImages(
            item.content.properties.text.markup,
          );
        }
        return item;
      },
    );
  }
  if (props.imagePicker) {
    props.imagePicker = formatImages(props.imagePicker);
  }

  // Example: generic images field
  if (props.images) {
    props.images = formatImages(props.images);
  }

  if (props.imageOrVideo) {
    props.imageOrVideo = formatImages(props.imageOrVideo);
    props.imageOrVideo = formatImages(
      Array.isArray(props.imageOrVideo)
        ? props.imageOrVideo
        : [props.imageOrVideo],
    );
  }

  // Process text/markup fields with embedded images
  if (props.text?.markup) {
    props.text.markup = normalizeMarkupImages(props.text.markup);
  }

  if (props.rteText?.markup) {
    props.rteText.markup = normalizeMarkupImages(props.rteText.markup);
  }

  // Process any other markup fields
  Object.keys(props).forEach((key) => {
    if (
      typeof props[key] === 'object' &&
      props[key] !== null &&
      'markup' in props[key]
    ) {
      props[key].markup = normalizeMarkupImages(props[key].markup);
    }
  });

  return block;
}

function formatImages(images?: any[]): any[] {
  if (!Array.isArray(images)) {
    return [];
  }

  return images.map((img) => ({
    ...img,
    url: getUmbracoImageUrl(img?.url),
  }));
}

function normalizeRoutePath(path?: string): string {
  return (path ?? '').replace(/^\/+|\/+$/g, '');
}

function normalizeFacilityType(value?: string): FacilityContentTypeFilter | '' {
  const normalized = (value ?? '').trim();

  if (
    normalized === 'Anläggning' ||
    normalized === 'Stad' ||
    normalized === 'Lan'
  ) {
    return normalized;
  }

  return '';
}
